import {
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Table,
  Tag,
  message,
} from "antd";
import axios from "axios";
import moment from "moment";
import React, { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import DatePicker from "../../../../../components/inputs/MomentDatePicker";
import { serverUrl } from "../../../../../nestserver";

const INQUIRY_QUERY_KEY = "parent-admission-inquiries";

const STATUS_META = {
  new: { label: "Received", color: "blue" },
  contacted: { label: "Contacted", color: "orange" },
  visited: { label: "Visited", color: "purple" },
  converted: { label: "Applied", color: "green" },
  closed: { label: "Closed", color: "default" },
};

const authHeaders = () => ({
  Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
});

// Parent side of admission inquiries — shared by the Senior and Montessori
// parent portals. A parent can raise an inquiry, follow the ones they raised,
// edit one until the school picks it up, and withdraw it until it is applied
// or closed (same rules as the mobile app; the backend enforces them).
const AdmissionInquiry = () => {
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const inquiries = useQuery(INQUIRY_QUERY_KEY, () =>
    axios
      .get(`${serverUrl}/admission-inquiry`, { headers: authHeaders() })
      .then((res) => res.data)
  );

  const classes = useQuery("classes", () =>
    axios
      .get(`${serverUrl}/classes`, { headers: authHeaders() })
      .then((res) => res.data)
  );

  const refresh = () => queryClient.invalidateQueries(INQUIRY_QUERY_KEY);

  const openModal = (record) => {
    setEditing(record || null);
    form.resetFields();
    if (record) {
      form.setFieldsValue({
        ...record,
        class: record.class?._id,
        dob: record.dob ? moment(record.dob, "YYYY-MM-DD") : undefined,
      });
    }
    setModalOpen(true);
  };

  const onWithdraw = async (record) => {
    try {
      const res = await axios.patch(
        `${serverUrl}/admission-inquiry/${record._id}/withdraw`,
        {},
        { headers: authHeaders() }
      );
      if (res?.data?.success === false) {
        message.error(res.data.message);
      } else {
        message.success(res?.data?.message || "Inquiry withdrawn");
      }
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Could not withdraw the inquiry"
      );
    }
    // Reload either way so the row shows what the school has done meanwhile.
    refresh();
  };

  const onSave = async (values) => {
    const payload = {
      ...values,
      // Plain calendar date — a date object would be serialised as UTC.
      dob: values.dob ? values.dob.format("YYYY-MM-DD") : "",
    };
    setSaving(true);
    try {
      const res = editing
        ? await axios.patch(
            `${serverUrl}/admission-inquiry/${editing._id}`,
            payload,
            { headers: authHeaders() }
          )
        : await axios.post(`${serverUrl}/admission-inquiry`, payload, {
            headers: authHeaders(),
          });
      if (res?.data?.success === false) {
        message.error(res.data.message);
        // The school may have picked it up since the list loaded.
        if (editing) refresh();
        return;
      }
      message.success(
        res?.data?.message || (editing ? "Inquiry updated" : "Inquiry sent")
      );
      setModalOpen(false);
      refresh();
    } catch (err) {
      message.error(
        err?.response?.data?.message ||
          (editing
            ? "Could not update the inquiry"
            : "Could not send the inquiry")
      );
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: "Child",
      key: "child",
      render: (_, r) => (
        <div>
          <div className="font-semibold capitalize">
            {r.first_name} {r.last_name}
          </div>
          {r.dob && (
            <div className="text-xs text-gray-500">
              DOB {moment(r.dob, "YYYY-MM-DD").format("DD-MM-YYYY")}
            </div>
          )}
        </div>
      ),
    },
    { title: "Class", key: "class", render: (_, r) => r.class?.name || "-" },
    {
      title: "Status",
      key: "status",
      render: (_, r) => {
        const meta = STATUS_META[r.status] || { label: r.status };
        return (
          <Tag color={meta.color}>
            {meta.label}
            {r.status === "converted" && r.application?.application_number
              ? ` · ${r.application.application_number}`
              : ""}
          </Tag>
        );
      },
    },
    {
      title: "Sent",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (v) => (v ? moment(v).format("DD-MM-YYYY") : "-"),
    },
    {
      title: "Action",
      key: "action",
      render: (_, r) => {
        // Edit only until the school picks it up; withdraw until applied/closed.
        const canEdit = r.status === "new";
        const canWithdraw = r.status !== "converted" && r.status !== "closed";
        if (!canEdit && !canWithdraw) return "-";
        return (
          <div className="flex gap-1 items-center">
            {canEdit && (
              <Button type="link" size="small" onClick={() => openModal(r)}>
                Edit
              </Button>
            )}
            {canWithdraw && (
              <Popconfirm
                title="Withdraw this inquiry?"
                description="The school will no longer follow it up."
                okText="Withdraw"
                cancelText="Keep"
                onConfirm={() => onWithdraw(r)}
              >
                <Button type="link" size="small" danger>
                  Withdraw
                </Button>
              </Popconfirm>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="mt-8">
      <div
        style={{ borderRadius: "10px", color: "#616161" }}
        className="bg-white drop-shadow-md p-8 w-full"
      >
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="text-2xl text-orange-500 font-semibold">
            Admission Inquiries
          </div>
          <Button type="primary" onClick={() => openModal()}>
            Add Inquiry
          </Button>
        </div>
        <div className="py-5">
          <Table
            rowKey="_id"
            columns={columns}
            dataSource={inquiries.data?.data || []}
            loading={inquiries.isLoading}
            pagination={{ pageSize: 10 }}
          />
        </div>
      </div>

      <Modal
        title={editing ? "Edit Inquiry" : "Admission Inquiry"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        destroyOnClose
        width={640}
      >
        <Form form={form} layout="vertical" onFinish={onSave}>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item
              name="first_name"
              label="Child First Name"
              rules={[
                { required: true, whitespace: true, message: "Required" },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="last_name" label="Child Last Name">
              <Input />
            </Form.Item>
            <Form.Item name="dob" label="Date Of Birth">
              <DatePicker
                className="w-full"
                format="DD-MM-YYYY"
                disabledDate={(current) =>
                  current && current > moment().endOf("day")
                }
              />
            </Form.Item>
            <Form.Item name="gender" label="Gender">
              <Select
                allowClear
                placeholder="Select gender"
                options={[
                  { value: "male", label: "Male" },
                  { value: "female", label: "Female" },
                  { value: "others", label: "Other" },
                ]}
              />
            </Form.Item>
            <Form.Item name="class" label="Class Interested In">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Select class"
                options={(classes.data || []).map((c) => ({
                  value: c._id,
                  label: c.name,
                }))}
              />
            </Form.Item>
            <Form.Item
              name="parent_name"
              label="Parent Name"
              rules={[
                { required: true, whitespace: true, message: "Required" },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="phone"
              label="Parent Phone"
              rules={[
                { required: true, message: "Required" },
                {
                  pattern: /^[0-9]{10}$/,
                  message: "Please enter a valid 10-digit phone number!",
                },
              ]}
              getValueFromEvent={(e) => e.target.value.slice(0, 10)}
            >
              <Input inputMode="numeric" maxLength={10} />
            </Form.Item>
            <Form.Item
              name="email"
              label="Parent Email"
              rules={[{ type: "email", message: "Please enter a valid email" }]}
            >
              <Input />
            </Form.Item>
          </div>
          <Form.Item name="address" label="Parent Address">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} />
          </Form.Item>
          <div className="flex justify-end gap-3">
            <Button onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={saving}>
              {editing ? "Save Changes" : "Send Inquiry"}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default AdmissionInquiry;
